import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { VIDEO_DURATIONS, VIDEO_TYPES, VIDEO_PLATFORMS, type VideoOutput } from "@shared/video.ts";

export default function Video() {
  const { activeBrand } = useBrands();
  const [f, setF] = useState<Record<string, string>>({ duration: "30 วิ", type: "Ads", platform: "TikTok" });
  const [out, setOut] = useState<VideoOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { load(); }, [activeBrand?.id]);
  async function load() { if (!activeBrand) return setHistory([]); const r = await api.get<{ videos: any[] }>(`/video?brandId=${activeBrand.id}`); setHistory(r.videos); }

  async function gen() {
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null);
    try { const r = await api.post<any>("/video/generate", { brandId: activeBrand.id, inputs: f }); if (r.error === "ai_unparsable") { setErr("ลองใหม่"); return; } setOut(r.output); setIsMock(r.isMock); load(); }
    catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ video: any }>(`/video/${id}`); setOut(r.video.data); setIsMock(false); } finally { setLoading(false); } }

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">ทำวิดีโอ <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Video Studio</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b></> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="Clapperboard" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid">
          <div className="mh-field" style={{ margin: 0, gridColumn: "1 / -1" }}><label className="mh-label">หัวข้อ/สินค้า</label><input className="mh-input" value={f.topic || ""} onChange={(e) => set("topic", e.target.value)} placeholder="เช่น คอร์สสอนยิงแอด" /></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">ความยาว</label><select className="mh-select" value={f.duration} onChange={(e) => set("duration", e.target.value)}>{VIDEO_DURATIONS.map((x) => <option key={x}>{x}</option>)}</select></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">ประเภท</label><select className="mh-select" value={f.type} onChange={(e) => set("type", e.target.value)}>{VIDEO_TYPES.map((x) => <option key={x}>{x}</option>)}</select></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">แพลตฟอร์ม</label><select className="mh-select" value={f.platform} onChange={(e) => set("platform", e.target.value)}>{VIDEO_PLATFORMS.map((x) => <option key={x}>{x}</option>)}</select></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={gen} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="Clapperboard" size={18} /> สร้างแผนวิดีโอ</>}</button>
          {history.length > 0 && <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}><option value="">📁 เดิม ({history.length})</option>{history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /></div>}
      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>แผนวิดีโอจาก <b>Mock</b> — ใส่ Claude Key เพื่อสคริปต์สร้างสรรค์จริง</div></div>}
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <KV k="ไอเดีย" v={out.idea} />
            <div style={{ background: "var(--blue-050)", borderRadius: 10, padding: 12, margin: "8px 0" }}>
              <div className="mh-muted" style={{ fontSize: 12 }}>🪝 Hook 1-3 วิ</div><div style={{ fontWeight: 700, fontSize: 16 }}>{out.hook3s}</div>
              <div className="mh-muted" style={{ fontSize: 12, marginTop: 8 }}>Hook อื่นๆ</div>
              <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>{out.hookVariations.map((h, i) => <li key={i} style={{ fontSize: 13.5 }}>{h}</li>)}</ul>
            </div>
          </div>

          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, marginBottom: 10 }}>🎬 Scene Breakdown</h3>
            <div style={{ display: "grid", gap: 8 }}>
              {out.scenes.map((sc, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 10, borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
                  <b style={{ color: "var(--blue-600)" }}>{sc.time}</b>
                  <div style={{ fontSize: 13.5 }}>
                    <div>🎥 {sc.visual}</div>
                    <div className="mh-muted">💬 {sc.onScreenText}</div>
                    <div className="mh-muted">🎙️ {sc.voiceOver}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-grid" style={{ alignItems: "start" }}>
            <ListCard title="📋 Shot List" items={out.shotList} />
            <ListCard title="🎞️ B-Roll" items={out.bRoll} />
          </div>
          <div className="mh-card card-pad" style={{ marginTop: 14 }}>
            <KV k="🎯 CTA" v={out.cta} /><KV k="🖼️ Thumbnail" v={out.thumbnail} /><KV k="📝 Caption" v={out.caption} />
            <div style={{ background: "var(--gray-050)", borderRadius: 8, padding: 10, marginTop: 4 }}><div className="mh-muted" style={{ fontSize: 12 }}>Video AI Prompt</div><div style={{ fontFamily: "var(--font-en)", fontSize: 13 }}>{out.videoAiPrompt}</div></div>
            <KV k="📱 ปรับตามแพลตฟอร์ม" v={out.platformAdaptation} />
          </div>
        </>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) { return <div style={{ marginBottom: 8 }}><div className="mh-muted" style={{ fontSize: 12.5 }}>{k}</div><div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{v}</div></div>; }
function ListCard({ title, items }: { title: string; items: string[] }) { return <div className="mh-card card-pad"><h3 style={{ fontSize: 14, marginBottom: 8 }}>{title}</h3><ul style={{ margin: 0, paddingLeft: 18 }}>{items.map((x, i) => <li key={i} style={{ marginBottom: 4, fontSize: 13.8 }}>{x}</li>)}</ul></div>; }
